import React, { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'

import { useAccounts } from '@modules-deprecated/app/accounts'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useTaxRates } from '@modules-deprecated/app/taxrates'

import { useFormContext, useWatch } from '../../../../../../../hooks'
import { getRegion } from '../../../../../../../utils/getRegion'
import {
  BookkeepingHeroSelect,
  BookkeepingTagNavItemValue,
  getAccountFromBookkeepingTagAnswer,
  getTaxRateFromBookkeepingTagAnswer,
} from '../../../../../../selects/BookkeepingHeroSelect'
import { ContactFormSchema } from '../../../../utils/formData'

interface ContactDefaultProductDescriptionProps {
  disabled?: boolean
}

export const ContactDefaultProductDescription = ({ disabled }: ContactDefaultProductDescriptionProps): ReactElement => {
  const { t } = useTranslation()
  const { control, FormItem, setValue, getValues } = useFormContext<ContactFormSchema>()
  const country = useWatch({ control, name: 'country' })
  const { organization } = useUserOrganization()
  const { accounts = [] } = useAccounts()
  const { taxRates = [] } = useTaxRates()

  const handleBookkeepingHeroTagSelect = useCallback(
    (bookkeepingTagValue?: BookkeepingTagNavItemValue) => {
      setValue('defaultBookkeepingTag', bookkeepingTagValue)

      if (!bookkeepingTagValue) {
        return
      }

      // Get current form values
      const autoCompletedFields = { ...getValues('autoCompletedFields') }
      const defaultExpenseAccount = getValues('defaultExpenseAccount')
      const paymentTermsMode = getValues('paymentTermsMode')

      // Get default data from bookeeping hero answer
      const answer = bookkeepingTagValue.response
      const account = answer ? getAccountFromBookkeepingTagAnswer(answer.accountId, accounts) : undefined

      const region = getRegion({
        vendorCountryCode: country?.id,
        organizationCountryCode: organization?.countryId,
      })

      const taxRate = answer
        ? getTaxRateFromBookkeepingTagAnswer({ answer: answer.vatRates, taxRates, region, hasVATdetected: undefined })
        : undefined

      // Set values if haven't been set OR different from the suggested ones
      if ((account && !defaultExpenseAccount?.id) || defaultExpenseAccount?.id !== account?.id) {
        setValue('defaultExpenseAccount', account)
        autoCompletedFields.defaultExpenseAccount = true
      }

      if (taxRate && !paymentTermsMode?.id) {
        setValue('paymentTermsMode', taxRate)
        autoCompletedFields.paymentTermsMode = true
      }

      // Wait one frame before setting autoCompletedFields
      window.requestAnimationFrame(() => {
        setValue('autoCompletedFields', autoCompletedFields)
      })
    },
    [accounts, country, getValues, organization?.countryId, setValue, taxRates],
  )

  /* training data for ML: only session id is mandatory, no contact to send yet, no scanned result */
  const trainingData = useMemo(() => ({ contact: undefined, id: uuidv4(), scannedCategoryResult: undefined }), [])

  return (
    <FormItem
      label={t('create_contact_modal.form.contact_standard_settings.description')}
      name="defaultProductDescription"
      render={({ field: { value, onChange, ...props } }) => (
        <BookkeepingHeroSelect
          disabled={disabled}
          inputValue={value}
          onChange={onChange}
          onSelect={handleBookkeepingHeroTagSelect}
          onClear={() => onChange('')}
          trainingData={trainingData}
          {...props}
        />
      )}
    />
  )
}
